export default {
  name: 'Jeffrey C. Ludden',
  title: 'Senior Software Engineer',
  description: 'Experienced web developer passionate about performance, usability, and responsive design.',
  linkUrl: 'https://jeffludden.com',
  qualities: {
    title: 'Qualifications',
    items: [
      {
        bullets: [
          'Fast and proficient at creating functional prototypes from designer specifications',
          'Excellent at debugging, including using developer tools, analyzing network traffic, proxying devices, identifying reproducible steps, etc.',
          'Experienced at developing cross-browser, cross-device solutions',
          'Proactive at developing internal tools to automate tasks and improve efficiency',
          'Adept at learning and implementing new technologies',
          'Keen eye for details, including designs and instructions',
          'Cross-functional team member, productive across remote teams and time zones',
          'Comfortable interfacing with executives, managers, product, QA, and customers',
        ]
      }
    ]  
  },
  experience: {
    title: 'Work History',
    items: [
      {
        dates: 'Nov 2018 to present',
        org: 'Apple Inc.',
        title: 'Senior Software Engineer',
        icon: require('@/assets/logos/apple-logo.svg'),
        icon_style: 'width: 30px; float:left;',
        bullets: [
          'Develop modularized features, pages, and core functionality for Shazam.com, a single page web app built in a homegrown JavaScript-based framework, serving client-side traffic from AWS S3 and bot traffic from Node.js, following Agile methodology',
          'Design and implement Webby-award-winning, patented (<a href="https://patents.google.com/patent/US10166472" target="_blank" title="US Patent 10166472">US 10166472</a>), synchronized play-at-home game experience for <em>Beat Shazam</em> TV show',
          'Design, develop, deploy, and monitor RESTful ES6/Node.js-based web services to support high-traffic spikes for <em>Beat Shazam</em> sweepstakes, dynamic shareable image creation, and metadata scraping',
          'Develop internal editor tools for production team to build experiences such as <em>Beat Shazam</em>, television tentpole experiences (e.g. Grammys), commercial partner campaigns, and other produced events',
          'Deploy and monitor builds using Amazon Web Services including EB, S3, IAM, and Route 53',
          'Proactively develop internal tools, including responsive design grid, beacon/stat transparency tool, automated screenshots, heat maps, Splunk dashboards, and Chrome extensions',
          'Diligently review production Splunk beacon data and dashboards to track, investigate, and mitigate issues',
          'Implement A/B tests: high-profile, in-app web views that mimic native functionality to measure and enhance user engagement',
          'Developed mySQL database application, utilizing Node.js, Knex, and Objection, including design of database structure and Objection models',
          'Managed successful transition from Shazam for Artists to Apple Music for Artists as cross-functional Technical Lead',
          'Enhanced existing Shazam for Artists application with Scala oAuth functionality and React components',
          'Configured automated testing framework for web and web services utilizing Node.js, PhantomJS, and Nightwatch',
          'Quickly learned Vue-based AppleTV+ web application and implemented Parental Controls for Apple Commerce team',
          'Created and presented "What is Shazam?" to multiple UCSD Extension International Programs Department student classes'
        ],
        secondary: {
          dates: 'Dec 2012 to Nov 2018',
          org: 'Shazam',
          title: 'Senior Software Engineer',
          icon: require('@/assets/logos/shazam-logo.svg'),
          icon_style: 'height: 30px; float:left;',
          note: '(acquired by Apple)'
        }
      },
      {
        dates: 'Nov 2009 to Nov 2012',
        org: 'Yahoo, Inc.',
        title: 'Senior Software Engineer',
        icon: require('@/assets/logos/yahoo-logo.svg'),
        icon_style: 'height: 45px; float:left; transform:translate3d(-10px, -7px, 0);',
        bullets: [
          'Designed and developed features for Yahoo! Mail utilizing JavaScript, HTML5 and CSS3',
          'Specialized in high performance web standards: caching, sprites, reducing payload size, minimizing network requests, etc.',
          'Investigated customer issues, debugged problems, found and implemented solutions',
          'Scrum master: lead team standup meetings, followed agile methodology, and unblocked developers',
          'Supported YUI team in development of YUI 3.0',
          'Updated Yahoo! Mail to support multi-attachment upload',
          'Proactively added features such as the "Reply <--> Reply All" button',
          'Built internal tool to aid team automated unit testing',
          'Wrote front-end automated tests using Selenium/Jenkins',
          'Created and presented "Web Performance 101" at USC, UCLA, and Columbia University as part of Yahoo!\'s recruitment outreach'
        ]
      },
      {
        dates: 'Jun 2001 to Jun 2009',
        org: 'Science Applications International Corporation',
        title: 'Director, Internal Operations',
        previousTitles: [
          {
            title: 'Deputy Director, Internet Services',
            dates: 'Mar 2007 to Mar 2008',
          },
          {
            title: 'Senior New Media Programmer',
            dates: 'Mar 2006 to Mar 2007',
          },
          {
            title: 'New Media Programmer',
            dates: 'Jun 2001 to Mar 2006',
          }
        ],
        icon: require('@/assets/logos/saic-logo.svg'),
        icon_style: 'height: 10px; float:left; transform:translateY(4px);',
        bullets: [
          'Designed, developed, and maintained customized secure, dynamic, and standards-compliant web solutions using ASP, ASP.net, ColdFusion MX, PHP, HTML, CSS, AJAX, and JavaScript integrating with MS SQL Server, MS Access, mySQL, and Oracle databases',
          'Developed and delivered projects, organized work teams, and managed budgets and schedules to develop and deliver dynamic web sites for SAIC line organizations, from conception through execution',
          'Mentored 5-person development team on technical decisions and professional development',
          'Designed and developed internal, web-based portal. Functionality included tracking of operational information, online document library, Job Tracker, Time Tracker (allowed detailed billing of $10M/year to be tracked at a more granular level than corporate Deltek software), calendars, staff workload, etc.',
          'Developed web-based tracking tool for <em>Section 508</em> page remediation for a 12,000 page site',
          'Led finance team; responsible for compiling various reports (variance, time-sold, overtime, overhead, residual and missing hours reports), budgets, budget reforecasts, and cost-impact analyses',
          'Provided general operations support: time-card administration, training, custom reporting, and other functions that support the overall operation',
        ]
      },
      {
        dates: 'Jul 2000 to Mar 2001',
        org: 'Etensity, Inc.',
        title: 'Developer',
        previousTitles: [
          {
            title: 'Associate Developer',
            dates: 'Jul 2000 to Feb 2001',
          }
        ],
        bullets: [
          'Consultant on multiple client e-commerce sites, utilizing ASP, SQL Server, JavaScript, HTML, and CSS',
        ]
      }  
    ],
  },
  education: {
    title: 'Education',
    items: [{
      title: 'University of Virginia',
      lines: [
        'Charlottesville, VA',
        'Aug 1996 to May 2000',
        'B.S., Computer Science',
      ]
    }],
  },
  community: {
    title: 'Community',
    items: [
      {
        title: 'UVA Alumni Club of San Diego',
        lines: ['President, 2011 to present']
      },
      {
        title: 'Boys & Girls Clubs of San Diego',
        lines: ['Volunteer, 2012 to 2017']
      },
      {
        title: 'FIRST Robotics',
        lines: ['Judge, 2007', 'Volunteer, 2008 to 2009']
      },
      {
        title: 'WAKA DC Potomac Division',
        lines: ['President, Spring 2005']
      },
      {
        title: 'Trigon Engineering Society',
        lines: ['President, Spring 2000']
      },
      {
        title: 'Special Olympics of Virginia',
        lines: ['Volunteer, 1993 to 1998']
      },
    ]
  },
  personal: {
    title: 'Contact',
    items: [
      {
        title: 'Phone',
        lines: ['571.212.5781'],
      },
      {
        title: 'Email',
        lines: ['<a href="mailto:ludden@gmail.com">Ludden@gmail.com</a>'],
      },
      {
        title: 'Website',
        lines: ['<a href="https://www.jeffludden.com">www.jeffludden.com</a>'],
      },
      {
        title: 'LinkedIn',
        lines: ['<a href="http://linkedin.com/in/jeffrey-ludden/" target="_blank">linkedin.com/in/jeffrey-ludden/'],
      },
    ],
  },
  skills: {
    title: 'Skills',
    items: [
      {
        title: 'JavaScript',
        rating: 4.5,
      },
      {
        title: 'Node.js',
        rating: 4.5,
      },
      {
        title: 'HTML5/CSS3',
        rating: 4.5,
      },
      {
        title: 'Debugging',
        rating: 5,
      },
      {
        title: 'Splunk',
        rating: 4,
      },
      {
        title: 'Charles',
        rating: 4,
      },
      {
        title: 'SQL/mySQL',
        rating: 4,
      },
      {
        title: 'Git',
        rating: 3.5,
      },
      {
        title: 'AWS',
        rating: 3.5,
      },
      {
        title: 'Gulp',
        rating: 3.5,
      },
      {
        title: 'NPM',
        rating: 3.5,
      },
      {
        title: 'Vue',
        rating: 3.5,
      },
      {
        title: 'PHP',
        rating: 3.5,
      },
      {
        title: 'Knex',
        rating: 3.5,
      },
      {
        title: 'React',
        rating: 3,
      },
    ]
  },
};